import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthApi } from '../context/ApiContext';
import { useNotification, NotificationType } from '../context/NotificationContext';
import { Shield, Mail, Phone, ArrowLeft, CheckCircle } from 'lucide-react';
import stewartLogo from '../assets/stewart_logo.png';

/**
 * DualAuth component for handling the dual authentication flow
 * This page is shown when a user needs to verify their identity
 */
const DualAuth = () => {
    // Hooks
    const location = useLocation();
    const navigate = useNavigate();
    const authApi = useAuthApi();
    const { showNotification } = useNotification();

    // State
    const [token, setToken] = useState<string>('');
    const [verificationCode, setVerificationCode] = useState<string>('');
    const [emailAddress, setEmailAddress] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [codeSent, setCodeSent] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [contactType, setContactType] = useState<'email' | 'phone' | null>(null);
    const [originalUrl, setOriginalUrl] = useState<string>('/');
    const [showContactForm, setShowContactForm] = useState<boolean>(false);

    // Parse parameters from URL on component mount
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const tokenParam = queryParams.get('token');
        const redirectParam = queryParams.get('redirect');

        if (tokenParam) {
            setToken(tokenParam);
        } else {
            // No token in URL, redirect to login
            navigate('/login');
        }

        if (redirectParam) {
            setOriginalUrl(redirectParam);
        }
    }, [location, navigate]);

    // Handle selecting contact method
    const selectContactMethod = (method: 'email' | 'phone') => {
        setContactType(method);
        setShowContactForm(true);
    };

    // Handle sending verification code
    const handleSendCode = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!token) {
            showNotification(NotificationType.ERROR, 'Invalid token');
            return;
        }

        // Validate contact information
        if (contactType === 'email' && !emailAddress) {
            showNotification(NotificationType.ERROR, 'Please enter your email address');
            return;
        }

        if (contactType === 'phone' && !phoneNumber) {
            showNotification(NotificationType.ERROR, 'Please enter your phone number');
            return;
        }

        setIsLoading(true);

        try {

            const request = {
                token: token,
                ...(contactType === 'phone' ? { phoneNumber } : {}),
                ...(contactType === 'email' ? { emailAddress } : {})
            }

            const response = await authApi.sendVerificationCode(request);

            if (response.ok) {
                setCodeSent(true);
                showNotification(NotificationType.SUCCESS, 'Verification code sent successfully');
            } else {
                showNotification(NotificationType.ERROR, response.message || 'Failed to send verification code');
            }
        } catch (error) {
            console.error('Error sending verification code:', error);
            showNotification(NotificationType.ERROR, 'Failed to send verification code');
        } finally {
            setIsLoading(false);
        }
    };

    // Handle verification code submission
    const handleVerifyCode = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!verificationCode || verificationCode.length !== 6) {
            showNotification(NotificationType.ERROR, 'Please enter a valid 6-digit verification code');
            return;
        }

        setIsLoading(true);

        try {
            const request = {
                token,
                code: verificationCode,
                // The backend will add user agent and IP address
            };

            const response = await authApi.verifyVerificationCode(request);

            if (response.ok) {
                showNotification(NotificationType.SUCCESS, 'Verification successful');

                // Redirect back to the original URL the user was trying to access
                navigate(originalUrl || '/');
            } else {
                showNotification(NotificationType.ERROR, response.message || 'Verification failed');
            }
        } catch (error) {
            console.error('Error verifying code:', error);
            showNotification(NotificationType.ERROR, 'Failed to verify code');
        } finally {
            setIsLoading(false);
        }
    };

    // Handle going back
    const handleBack = () => {
        if (codeSent) {
            setCodeSent(false);
            setVerificationCode('');
        } else if (showContactForm) {
            setShowContactForm(false);
            setContactType(null);
        }
    };

    // Format phone number as user types
    const handlePhoneChange = (value: string) => {
        // Strip non-numeric characters
        const numbers = value.replace(/[^\d]/g, '');

        // Format with dashes
        if (numbers.length <= 3) {
            setPhoneNumber(numbers);
        } else if (numbers.length <= 6) {
            setPhoneNumber(`${numbers.slice(0, 3)}-${numbers.slice(3)}`);
        } else {
            setPhoneNumber(`${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`);
        }
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-4">
            {/* Logo */}
            <div className="mb-8">
                <img
                    src={stewartLogo}
                    alt="Stewart Logo"
                    className="h-12 sm:h-16"
                />
            </div>

            {/* Authentication Card */}
            <div className="bg-white rounded-lg shadow-md border border-slate-200 w-full max-w-md p-6">
                <div className="flex items-center justify-center mb-6">
                    <Shield className="h-8 w-8 text-blue-600 mr-2" />
                    <h1 className="text-2xl font-semibold text-slate-800">
                        Verify Your Identity
                    </h1>
                </div>

                {!showContactForm && !codeSent ? (
                    // Step 1: Select verification method
                    <div>
                        <p className="text-slate-600 mb-6 text-center">
                            For security reasons, we need to verify your identity.
                            Please choose how you'd like to receive your verification code:
                        </p>

                        <div className="space-y-4">
                            <button
                                onClick={() => selectContactMethod('email')}
                                disabled={isLoading}
                                className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-blue-600 hover:bg-blue-700
                                          text-white rounded-md transition-colors duration-200 disabled:opacity-50"
                            >
                                <Mail className="h-5 w-5" />
                                Verify with Email
                            </button>

                            <button
                                onClick={() => selectContactMethod('phone')}
                                disabled={isLoading}
                                className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-blue-600 hover:bg-blue-700
                                          text-white rounded-md transition-colors duration-200 disabled:opacity-50"
                            >
                                <Phone className="h-5 w-5" />
                                Verify with Phone
                            </button>
                        </div>
                    </div>
                ) : showContactForm && !codeSent ? (
                    // Step 2: Enter contact information
                    <div>
                        <p className="text-slate-600 mb-4 text-center">
                            Please enter your {contactType === 'email' ? 'email address' : 'phone number'} to receive a verification code:
                        </p>

                        <form onSubmit={handleSendCode} className="space-y-4">
                            {contactType === 'email' ? (
                                <div>
                                    <label htmlFor="email-address" className="block text-sm font-medium text-slate-700 mb-1">
                                        Email Address
                                    </label>
                                    <input
                                        id="email-address"
                                        type="email"
                                        value={emailAddress}
                                        onChange={(e) => setEmailAddress(e.target.value)}
                                        placeholder="you@example.com"
                                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2
                                                focus:ring-blue-500 focus:border-blue-500"
                                        autoFocus
                                        required
                                    />
                                </div>
                            ) : (
                                <div>
                                    <label htmlFor="phone-number" className="block text-sm font-medium text-slate-700 mb-1">
                                        Phone Number
                                    </label>
                                    <input
                                        id="phone-number"
                                        type="tel"
                                        value={phoneNumber}
                                        onChange={(e) => handlePhoneChange(e.target.value)}
                                        placeholder="123-456-7890"
                                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2
                                                focus:ring-blue-500 focus:border-blue-500"
                                        autoFocus
                                        required
                                    />
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={isLoading || (contactType === 'email' ? !emailAddress : !phoneNumber)}
                                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-blue-600 hover:bg-blue-700
                                        text-white rounded-md transition-colors duration-200 disabled:opacity-50"
                            >
                                <Mail className="h-5 w-5" />
                                {isLoading ? 'Sending...' : 'Send Verification Code'}
                            </button>

                            <button
                                type="button"
                                onClick={handleBack}
                                className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-transparent text-slate-600
                                        hover:bg-slate-100 rounded-md transition-colors duration-200 mt-2"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Back
                            </button>
                        </form>
                    </div>
                ) : (
                    // Step 3: Enter verification code
                    <div>
                        <p className="text-slate-600 mb-4 text-center">
                            We've sent a verification code to your {contactType === 'email' ? 'email address' : 'phone number'}.
                        </p>

                        <form onSubmit={handleVerifyCode} className="space-y-4">
                            <div>
                                <label htmlFor="verification-code" className="block text-sm font-medium text-slate-700 mb-1">
                                    Enter 6-Digit Code
                                </label>
                                <input
                                    id="verification-code"
                                    type="text"
                                    value={verificationCode}
                                    onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                                    placeholder="123456"
                                    maxLength={6}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2
                                            focus:ring-blue-500 focus:border-blue-500 text-center text-lg tracking-wider"
                                    autoFocus
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading || verificationCode.length !== 6}
                                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-blue-600 hover:bg-blue-700
                                        text-white rounded-md transition-colors duration-200 disabled:opacity-50"
                            >
                                <CheckCircle className="h-5 w-5" />
                                {isLoading ? 'Verifying...' : 'Verify Code'}
                            </button>

                            <button
                                type="button"
                                onClick={handleBack}
                                className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-transparent text-slate-600
                                        hover:bg-slate-100 rounded-md transition-colors duration-200 mt-2"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Back
                            </button>
                        </form>

                        <div className="mt-4">
                            <p className="text-sm text-slate-500 text-center">
                                Didn't receive a code?{' '}
                                <button
                                    onClick={handleSendCode}
                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                    Resend
                                </button>
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="mt-8 text-center">
                <p className="text-sm text-slate-500">
                    © {new Date().getFullYear()} Stewart Valuation Intelligence
                </p>
            </div>
        </div>
    );
};

export default DualAuth;